import React from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';

interface Step1BasicInfoProps {
    formData: any;
    setFormData: (data: any) => void;
    file: File | null;
    fileName: string;
    onFileChange: (file: File) => void;
    documentTypeOptions: any[];
    onShowEditor?: () => void;
    onEditEditor?: () => void;
    editorCreating?: boolean;
    fileId?: string | null;
    readonly?: boolean;
    errors?: any;
}

export const Step1BasicInfo: React.FC<Step1BasicInfoProps> = ({
    formData,
    setFormData,
    file,
    fileName,
    onFileChange,
    documentTypeOptions,
    onShowEditor,
    onEditEditor,
    editorCreating,
    fileId,
    readonly = false,
    errors = {}
}) => {
    const currentFileName = fileName || file?.name || '';
    const isImportant = formData?.isImportant === 'Y' || formData?.isImportant === true;
    const isUrgent = formData?.isUrgent === 'Y' || formData?.isUrgent === true;

    const togglePriority = (field: 'isImportant' | 'isUrgent') => {
        if (readonly) return;
        const currentValue = formData?.[field] === 'Y' || formData?.[field] === true;
        setFormData({ ...formData, [field]: currentValue ? 'N' : 'Y' });
    };

    const openFilePicker = () => {
        if (readonly) return;
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.docx,.doc';
        input.onchange = (event: any) => {
            const selectedFile = event.target.files?.[0];
            if (selectedFile) onFileChange(selectedFile);
        };
        input.click();
    };

    return (
        <div className="template-step1-layout mx-auto">
            <div className="template-step1-main">
                <div className="mb-3">
                    <h2 className="m-0 text-xl">Tài liệu {currentFileName ? '(1)' : '(0)'}</h2>
                    <div className="text-sm mt-2">
                        Mức độ quan trọng tài liệu:
                        <button
                            type="button"
                            className={`template-priority-option ml-2 ${isImportant ? 'text-orange-500 font-semibold' : 'text-500'}`}
                            onClick={() => togglePriority('isImportant')}
                            disabled={readonly}
                        >
                            <i className="pi pi-bookmark-fill mr-1" />Quan trọng
                        </button>
                        <button
                            type="button"
                            className={`template-priority-option ml-3 ${isUrgent ? 'text-red-500 font-semibold' : 'text-500'}`}
                            onClick={() => togglePriority('isUrgent')}
                            disabled={readonly}
                        >
                            <i className="pi pi-bolt mr-1" />Khẩn cấp
                        </button>
                    </div>
                </div>

                <div className={`template-upload-panel ${errors.file ? 'border-red-400' : ''}`}>
                    <div className="template-upload-dropzone">
                        <i className="pi pi-cloud-upload text-primary" style={{ fontSize: 34 }} />
                        <div className="font-semibold text-primary mt-3">Thả tài liệu vào đây hoặc tải lên từ máy tính</div>
                        <div className="flex flex-wrap justify-content-center gap-3 mt-4">
                            <Button label="Máy tính" icon="pi pi-desktop" outlined severity="secondary" onClick={openFilePicker} disabled={readonly} />
                        </div>
                        <div className="text-600 mt-4">Định dạng được hỗ trợ: pdf, docx, doc</div>
                    </div>

                    {currentFileName && (
                        <div className="template-file-row">
                            <i className="pi pi-bars text-500" />
                            <div className="template-file-icon">W</div>
                            <div className="flex-1">
                                <div className="font-bold">{currentFileName}</div>
                                <div className="text-sm text-500">{file ? `${Math.max(1, Math.round(file.size / 1024))} KB` : 'Tệp mẫu'}</div>
                            </div>
                            {!readonly && onEditEditor && fileId && (
                                <Button icon="pi pi-pencil" text severity="warning" onClick={onEditEditor} title="Chỉnh sửa nội dung" />
                            )}
                        </div>
                    )}
                </div>
                {errors.file && <small className="p-error block mt-1">{errors.file}</small>}
            </div>

            <div className="template-step1-side surface-100 border-round p-4">
                <div className="field">
                    <label className="font-bold block mb-2">Tên mẫu<span className="text-red-500">*</span></label>
                    <InputText
                        value={formData.agreementName}
                        onChange={event => setFormData({ ...formData, agreementName: event.target.value })}
                        className={`w-full ${errors.agreementName ? 'p-invalid' : ''}`}
                        disabled={readonly}
                    />
                    {errors.agreementName && <small className="p-error">{errors.agreementName}</small>}
                </div>
                <div className="field">
                    <label className="font-bold block mb-2">Mã tài liệu mẫu<span className="text-red-500">*</span></label>
                    <InputText
                        value={formData.agreementPseudoId}
                        onChange={event => setFormData({ ...formData, agreementPseudoId: event.target.value })}
                        className={`w-full ${errors.agreementPseudoId ? 'p-invalid' : ''}`}
                        disabled={readonly}
                    />
                    {errors.agreementPseudoId && <small className="p-error block mt-1">{errors.agreementPseudoId}</small>}
                </div>
                <div className="field">
                    <label className="font-bold block mb-2">Loại tài liệu</label>
                    <Dropdown
                        value={formData.agreementPartyTypeConfId}
                        options={documentTypeOptions}
                        filter
                        filterBy="label"
                        showClear
                        onChange={event => setFormData({ ...formData, agreementPartyTypeConfId: event.value })}
                        className={`w-full ${errors.agreementPartyTypeConfId ? 'p-invalid' : ''}`}
                        placeholder="-- Không chọn --"
                        disabled={readonly}
                    />
                    {errors.agreementPartyTypeConfId && <small className="p-error">{errors.agreementPartyTypeConfId}</small>}
                </div>
                <div className="field">
                    <label className="font-bold block mb-2">Mô tả</label>
                    <InputTextarea
                        value={formData.description}
                        onChange={event => setFormData({ ...formData, description: event.target.value })}
                        rows={4}
                        className="w-full"
                        disabled={readonly}
                    />
                </div>
                {!readonly && onShowEditor && !fileId && (
                    <Button
                        label="Soạn mới"
                        icon={editorCreating ? 'pi pi-spin pi-spinner' : 'pi pi-file-edit'}
                        outlined
                        className="w-full"
                        onClick={onShowEditor}
                        disabled={editorCreating}
                    />
                )}
            </div>
        </div>
    );
};
